'use client';

import { RepeatIcon } from '@/_assets/icon-components';
import PhoneOrder from '@/_assets/icon-components/phone-order';
import { ISingleOrder } from '@/_service/types/orders';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/navigation';
import {
  Box,
  Center,
  Flex,
  HStack,
  Separator,
  SimpleGrid,
  Stack,
  StackSeparator,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { Rating, RoundedStar } from '@smastrom/react-rating';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import StepStatus from '../../StepStatus';
import OrderDetails from '../../components/OrderDetail';
import ProductCard from '../../components/ProductCard';
import CourierMap from './map';
import OrderInfo from './order-info';
import { ExternalLinkIcon } from 'lucide-react';
import { getBrandHref } from '@/_lib/brand-href';

const PreviewDesktop = ({
  id,
  sent,
  order,
  total,
  change,
  status,
  refetch,
  onSubmitReview,
  renderStatusText,
}: {
  id: string;
  total: number;
  change: number;
  sent: boolean;
  order: ISingleOrder;
  status: string;
  refetch: () => void;
  onSubmitReview: (payload: {
    rate: number;
    reviewMsg: string;
  }) => void;
  renderStatusText: () => React.ReactElement;
}) => {
  const t = useTranslations();
  const [rate, setRate] = useState(5);
  const [reviewMsg, setReviewMsg] = useState('');
  const [showMore, setShowMore] = useState(true);

  return (
    <Box bg={'background.card'} rounded={16}>
      <VStack alignItems='stretch' p={4} gap='4'>
        <OrderInfo order={order} total={total} />

        <Box alignSelf='center'>
          <StepStatus order={order} />

          <Center>
            {(status === 'finished' || status === 'delivered') &&
              renderStatusText()}
          </Center>

          <Box mt='4'>
            {((order.payment_type === 'online' && !order.paid) ||
              (status !== 'cancelled' && status !== 'finished')) &&
              status !== 'finished' &&
              status !== 'delivered' &&
              status !== 'cancelled' &&
              renderStatusText()}
          </Box>
        </Box>

        {status === 'courier-picked' && (
          <Box
            position='relative'
            w='full'
            transition='all 300ms ease-in-out'
            overflow='hidden'
            zIndex={1}
            h='270px'
            py='4'
            borderTop='1px solid'
            borderColor='background'
          >
            <CourierMap order={order} />
          </Box>
        )}

        <SimpleGrid
          w={'full'}
          columns={
            1 +
            Number(Boolean(order?.courier?.phone) || 0) +
            Number((status === 'delivered' || status === 'finished') || (status !== 'finished' &&
              status !== 'delivered' &&
              status !== 'cancelled' &&
              order?.payment_link ? 1 : 0) || 0)
          }
          gap={3}
        >
          {order?.courier?.phone && (
            <Button
              color={'text'}
              height={'71px'}
              fontWeight={400}
              variant='subtle'
              asChild
            >
              <Link href={'tel:' + order?.courier?.phone}>
                <Stack align='center' gap={1}>
                  <PhoneOrder />
                  <Text>{t('call_the_courier')}</Text>
                </Stack>
              </Link>
            </Button>
          )}

          <Button fontWeight={400} variant='subtle' h='75px' asChild>
            <Link
              href={'tel:+998712008989'}
            >
              <Stack align='center' gap={2} py='4'>
                <PhoneOrder />
                <Text color='text'>71 200 89 89</Text>
              </Stack>
            </Link>
          </Button>
          {(status === 'delivered' || status === 'finished') && (
            <Button
              color={'text'}
              height={'75px'}
              fontWeight={400}
              variant='subtle'
              asChild
            >
              <Link
                href={getBrandHref(order?.shipper_id, {
                  path: '/checkout',
                  query: { repeat: 'true', oid: id },
                })}
              >
                <Stack align='center' gap={1}>
                  <RepeatIcon />
                  <Text>{t('repeat')}</Text>
                </Stack>
              </Link>
            </Button>
          )}
          {status !== 'finished' &&
            status !== 'delivered' &&
            status !== 'cancelled' &&
            order?.payment_link && (
              <Button
                color={'text'}
                height={'75px'}
                fontWeight={400}
                variant='subtle'
                asChild
              >
                <Link href={order?.payment_link}>
                  <Stack align='center' gap={1}>
                    <ExternalLinkIcon />
                    <Text>{t('to_pay')}</Text>
                  </Stack>
                </Link>
              </Button>
            )}
        </SimpleGrid>
      </VStack>

      <Separator size={'lg'} my={2} borderColor={'background'} />

      <Box pt='6' pb='4'>
        <Text px={6} fontSize={20} fontWeight={700}>
          {t('order_composition')}
        </Text>
        <Stack separator={<StackSeparator />} gap={0}>
          {order?.steps?.[0].products.map((product) => (
            <Box px={2} key={product.id}>
              <ProductCard
                refetch={refetch}
                key={product.id}
                product={product}
                orderData={order}
              />
            </Box>
          ))}
        </Stack>

        <Box px={6}>
          {order?.description && (
            <Flex
              flexDir={'column'}
              gap={3}
              bg={'background'}
              p={4}
              rounded={16}
            >
              <Text fontSize={18} fontWeight={500}>
                {t('description_to_the_order')}
              </Text>
              <Text fontSize={18} fontWeight={400}>
                {order?.description}
              </Text>
            </Flex>
          )}
        </Box>
      </Box>

      <Separator size={'lg'} borderColor={'background'} />

      <Box mt={6}>
        <OrderDetails order={order} total={total} change={change} />
      </Box>

      <Box mb={3}>
        {sent ? (
          <Alert status='success' title={t('successfully_sent')} />
        ) : (
          (status === 'delivered' || status === 'finished') && (
            <>
              <Separator py={2} size={'lg'} borderColor={'background'} />
              <Box p={4}>
                <HStack
                  align='center'
                  justify='space-between'
                  mb={!order?.review ? 4 : 0}
                >
                  <Text fontSize={20} fontWeight={700}>
                    {t('rate')}
                  </Text>
                  {!order?.review && (
                    <Rating
                      readOnly={Boolean(order?.review_id)}
                      style={{ maxWidth: 100 }}
                      value={!order?.review_id ? rate : order?.rating}
                      itemStyles={{
                        itemShapes: RoundedStar,
                        activeFillColor: '#FFC500',
                        inactiveFillColor: '#E0E8F1',
                      }}
                      onChange={(newValue: number) => {
                        setRate(newValue);
                      }}
                    />
                  )}
                </HStack>
                <Stack>
                  {!order?.review_id ? (
                    <Stack gap={4}>
                      <Textarea
                        size={'xl'}
                        rounded={16}
                        height={'100px'}
                        bg={'background'}
                        variant='subtle'
                        outline={'none'}
                        focusRingColor={'primary'}
                        placeholder={t('your_feedback')}
                        value={reviewMsg}
                        onChange={(e) => setReviewMsg(e.target.value)}
                      />
                      <Button
                        mt={3}
                        onClick={() =>
                          onSubmitReview({
                            rate,
                            reviewMsg,
                          })
                        }
                        bg='primary'
                        color='primary.text'
                        _hover={{ bg: 'primary.hover' }}
                        size={'2xl'}
                        height={'56px'}
                        disabled={rate < 1}
                        loading={sent}
                      >
                        {t('send')}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack p={2} mt={2} borderRadius={8} bg={'#F9FAFB'}>
                      <Text fontSize={'md'} fontWeight={500}>{t('your_feedback')}</Text>
                      <Box>
                        <Rating
                          readOnly={Boolean(order?.review)}
                          style={{ maxWidth: 100 }}
                          value={!order?.review_id ? rate : order?.rating}
                          itemStyles={{
                            itemShapes: RoundedStar,
                            activeFillColor: '#FFC500',
                            inactiveFillColor: '#E0E8F1',
                          }}
                        />
                        <Text mt={2} fontSize={'sm'}>
                          {showMore
                            ? order?.review
                            : order?.review?.substring(0, 140) + '...'}
                        </Text>
                        {order?.review?.length > 140 && (
                          <Button
                            w='fit-content'
                            color={'primary.500'}
                            fontWeight={400}
                            transform={`translateY(${showMore ? 0 : -5}px)`}
                            onClick={() => setShowMore((prev) => !prev)}
                          >
                            {showMore ? t('show_less') : t('show_more')}
                          </Button>
                        )}
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </>
          )
        )}
      </Box>
      {status !== 'finished' &&
        status !== 'delivered' &&
        status !== 'cancelled' &&
        order?.payment_link && (
          <Box py={4} px={6}>
            <Link href={order?.payment_link}>
              <Button
                w={'full'}
                bg='primary'
                color='primary.text'
                _hover={{ bg: 'primary.hover' }}
                size={'xl'}
                h={'56px'}
              >
                {t('to_pay')}
              </Button>
            </Link>
          </Box>
        )}
    </Box>
  );
};

export default PreviewDesktop;
